import {
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import {
  TimePicker as RSuiteTimePicker,
  type TimePickerProps as RSuiteTimePickerProps,
} from "rsuite";
import type { FieldError } from "react-hook-form";
import { Label } from "./label";
import { Typography } from "./Typography";
import { cn } from "../lib/utils";

export type TimePickerProps = Omit<
  RSuiteTimePickerProps,
  "value" | "onChange" | "error"
> & {
  label?: ReactNode;
  required?: boolean;
  error?: string | FieldError | boolean | null;
  value?: string | Date | null;
  onChange?: (time24: string, date: Date | null) => void;
  containerClassName?: string;
};

const parseTimeToDate = (timeVal?: string | Date | null): Date | null => {
  if (!timeVal) return null;
  if (timeVal instanceof Date && !isNaN(timeVal.getTime())) return timeVal;
  if (typeof timeVal !== "string") return null;

  const trimmed = timeVal.trim();
  if (!trimmed) return null;

  const match = trimmed.match(
    /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([aApP][mM])?$/,
  );

  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = match[3] ? parseInt(match[3], 10) : 0;
    const meridiem = match[4]?.toUpperCase();

    if (meridiem === "PM" && hours < 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;

    if (
      hours >= 0 &&
      hours <= 23 &&
      minutes >= 0 &&
      minutes <= 59 &&
      seconds >= 0 &&
      seconds <= 59
    ) {
      const date = new Date();
      date.setHours(hours, minutes, seconds, 0);
      return date;
    }
  }

  const parsed = new Date(trimmed);
  return !isNaN(parsed.getTime()) ? parsed : null;
};

const formatDateTo24hTime = (date: Date | null): string => {
  if (!date || isNaN(date.getTime())) return "";
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const TimePicker = memo(
  forwardRef<HTMLDivElement, TimePickerProps>(
    (
      {
        label,
        required,
        error,
        value,
        onChange,
        format = "hh:mm a",
        showMeridiem = true,
        editable = true,
        cleanable = false,
        disabled = false,
        placeholder = "hh:mm aa",
        className,
        containerClassName,
        placement = "bottomStart",
        container,
        popupClassName,
        ...restProps
      },
      ref,
    ) => {
      const errorMessage =
        typeof error === "string"
          ? error
          : error && typeof error === "object" && "message" in error
            ? (error.message as string)
            : undefined;
      const hasError = Boolean(error);
      const parsedDate = useMemo(() => parseTimeToDate(value), [value]);
      const wrapperRef = useRef<HTMLDivElement | null>(null);

      const setRef = useCallback(
        (node: HTMLDivElement | null) => {
          wrapperRef.current = node;
          if (typeof ref === "function") {
            ref(node);
          } else if (ref && "current" in ref) {
            (ref as React.MutableRefObject<HTMLDivElement | null>).current =
              node;
          }
        },
        [ref],
      );

      const getPopupContainer = useCallback((): HTMLElement => {
        if (container) {
          return typeof container === "function" ? container() : container;
        }
        const dialogEl = wrapperRef.current?.closest<HTMLElement>(
          '[role="dialog"], [data-radix-portal], .dialog-content',
        );
        return (
          dialogEl ??
          (typeof document !== "undefined"
            ? (document.body ?? document.documentElement)
            : null) as unknown as HTMLElement
        );
      }, [container]);

      const handleTimeChange = useCallback(
        (nextDate: Date | null) => {
          if (!nextDate || isNaN(nextDate.getTime())) {
            onChange?.("", null);
            return;
          }
          onChange?.(formatDateTo24hTime(nextDate), nextDate);
        },
        [onChange],
      );

      return (
        <div className={cn("flex flex-col gap-1 w-full", containerClassName)}>
          {label && (
            <Label className="text-sm font-medium">
              {label}{" "}
              {required && (
                <span className="text-destructive text-xl align-middle">*</span>
              )}
            </Label>
          )}

          <div
            ref={setRef}
            className={cn(
              "custom-timepicker-wrapper w-full",
              hasError && "has-error",
              className,
            )}
          >
            <RSuiteTimePicker
              format={format}
              showMeridiem={showMeridiem}
              editable={editable}
              cleanable={cleanable}
              disabled={disabled}
              placeholder={placeholder}
              placement={placement}
              container={getPopupContainer}
              popupClassName={cn("custom-timepicker-popup", popupClassName)}
              value={parsedDate}
              onChange={handleTimeChange}
              ranges={[]}
              className="w-full"
              {...restProps}
            />
          </div>

          {errorMessage && (
            <Typography variant="error" className="text-xs text-destructive">
              {errorMessage}
            </Typography>
          )}
        </div>
      );
    },
  ),
);

TimePicker.displayName = "TimePicker";

export default TimePicker;
